/**
 * Marketplace Page - Main function discovery and commerce interface
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import FunctionCard from '../components/marketplace/FunctionCard';
import FunctionSearch from '../components/marketplace/FunctionSearch';
import {
  marketplaceService,
  FunctionListing,
  FunctionCategory,
  SearchParams,
  MarketplaceStats,
} from '../services/marketplace';
import toast from 'react-hot-toast';
import { AppShell } from '@/components';
import { MarketingSection, SectionHeader, GlassPanel } from '@/components/marketing';
import {
  SparklesIcon,
  ArrowTrendingUpIcon,
  TagIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import styles from './Marketplace.module.css';

const sanitizeFilters = (filters: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== null),
  );

const Marketplace: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [functions, setFunctions] = useState<FunctionListing[]>([]);
  const [categories, setCategories] = useState<FunctionCategory[]>([]);
  const [featuredFunctions, setFeaturedFunctions] = useState<FunctionListing[]>([]);
  const [stats, setStats] = useState<MarketplaceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [wishlistItems, setWishlistItems] = useState<Set<string>>(new Set());

  const initialSearchParams = useMemo<SearchParams>(() => {
    const rawFilters = {
      category: searchParams.get('category') || undefined,
      pricing: (searchParams.get('pricing') as any) || undefined,
      minRating: searchParams.get('rating') ? Number(searchParams.get('rating')) : undefined,
      featured: searchParams.get('featured') === 'true' ? true : undefined,
    };

    const filters = sanitizeFilters(rawFilters) as SearchParams['filters'];

    return {
      query: searchParams.get('q') || undefined,
      filters: Object.keys(filters ?? {}).length ? filters : undefined,
      sortBy: (searchParams.get('sort') as any) || 'relevance',
    };
  }, [searchParams]);

  const updateUrlParams = useCallback(
    (params: SearchParams) => {
      const newSearchParams = new URLSearchParams();

      if (params.query) newSearchParams.set('q', params.query);
      if (params.sortBy && params.sortBy !== 'relevance') newSearchParams.set('sort', params.sortBy);

      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value !== undefined) {
            if (Array.isArray(value)) {
              value.forEach((entry) => newSearchParams.append(key, entry));
            } else {
              newSearchParams.set(key, value.toString());
            }
          }
        });
      }

      setSearchParams(newSearchParams);
    },
    [setSearchParams],
  );

  const handleSearch = useCallback(
    async (params: SearchParams) => {
      setSearchLoading(true);
      setCurrentPage(1);

      try {
      const response = await marketplaceService.searchFunctions({
        ...params,
        page: 1,
        limit: 20,
      });

      if (response.status === 'success' && response.data) {
        const data = response.data;
        setFunctions(data.functions);
        setTotalResults(data.total);
        setHasMore(data.hasMore);
        setCurrentPage(1);
        updateUrlParams(params);
      } else {
        toast.error('Search failed');
      }
      } catch (error) {
        toast.error('Search failed');
        console.error('Search error:', error);
      } finally {
        setSearchLoading(false);
      }
    },
    [setFunctions, setTotalResults, setHasMore, setCurrentPage, updateUrlParams],
  );

  const loadInitialData = useCallback(async () => {
    setLoading(true);

    try {
      const [overviewResponse, featuredResponse] = await Promise.all([
        marketplaceService.getMarketplaceOverview(),
        marketplaceService.getFeaturedFunctions(12),
      ]);

      if (overviewResponse.status === 'success' && overviewResponse.data) {
        setCategories(overviewResponse.data.categories);
        setStats(overviewResponse.data.stats);
      } else {
        setCategories([]);
        setStats(null);
      }

      if (featuredResponse.status === 'success' && Array.isArray(featuredResponse.data)) {
        setFeaturedFunctions(featuredResponse.data);
      } else {
        setFeaturedFunctions([]);
      }

      try {
        const wishlistResponse = await marketplaceService.getWishlist(1, 100);
        if (
          wishlistResponse.status === 'success' &&
          wishlistResponse.data &&
          Array.isArray(wishlistResponse.data.functions)
        ) {
          const wishlistIds = new Set(wishlistResponse.data.functions.map((f) => f.id));
          setWishlistItems(wishlistIds);
        }
      } catch (error) {
        // User might not be authenticated; ignore.
      }
    } catch (error) {
      toast.error('Failed to load marketplace data');
      console.error('Marketplace load error:', error);
    } finally {
      setLoading(false);
    }
  }, [handleSearch, setCategories, setFeaturedFunctions, setWishlistItems]);

  useEffect(() => {
    void loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (!loading) {
      void handleSearch(initialSearchParams);
    }
  }, [handleSearch, initialSearchParams, loading]);

  const loadMore = async () => {
    if (!hasMore || searchLoading) return;

    setSearchLoading(true);
    const nextPage = currentPage + 1;

    try {
      const currentParams = getCurrentSearchParams();
      const response = await marketplaceService.searchFunctions({
        ...currentParams,
        page: nextPage,
        limit: 20,
      });

      if (response.status === 'success' && response.data) {
        const data = response.data;
        setFunctions((prev) => [...prev, ...data.functions]);
        setCurrentPage(nextPage);
        setHasMore(data.hasMore);
      }
    } catch (error) {
      toast.error('Failed to load more functions');
    } finally {
      setSearchLoading(false);
    }
  };

  const getCurrentSearchParams = (): SearchParams => {
    const rawFilters = {
      category: searchParams.get('category') || undefined,
      pricing: (searchParams.get('pricing') as any) || undefined,
      minRating: searchParams.get('rating') ? Number(searchParams.get('rating')) : undefined,
      featured: searchParams.get('featured') === 'true' ? true : undefined,
    };

    const filters = sanitizeFilters(rawFilters) as SearchParams['filters'];

    return {
      query: searchParams.get('q') || undefined,
      filters: Object.keys(filters ?? {}).length ? filters : undefined,
      sortBy: (searchParams.get('sort') as any) || 'relevance',
    };
  };

  const handlePurchase = async (functionId: string) => {
    try {
      const response = await marketplaceService.purchaseFunction({ functionId });

      if (response.status === 'success' && response.data) {
        toast.success('Function purchased successfully!');
      } else {
        toast.error('Purchase failed');
      }
    } catch (error) {
      toast.error('Purchase failed');
      console.error('Purchase error:', error);
    }
  };

  const handleWishlist = async (functionId: string, add: boolean) => {
    try {
      if (add) {
        await marketplaceService.addToWishlist(functionId);
        setWishlistItems((prev) => new Set(prev).add(functionId));
        toast.success('Added to wishlist');
      } else {
        await marketplaceService.removeFromWishlist(functionId);
        setWishlistItems((prev) => {
          const next = new Set(prev);
          next.delete(functionId);
          return next;
        });
        toast.success('Removed from wishlist');
      }
    } catch (error) {
      toast.error('Wishlist operation failed');
    }
  };

  const handlePreview = (functionId: string) => {
    window.open(`/marketplace/functions/${functionId}`, '_blank');
  };

  const totalFunctionsAvailable = stats?.totalFunctions ??
    (Array.isArray(categories)
      ? categories.reduce((sum, cat) => sum + (Number.isFinite(cat.count) ? cat.count : 0), 0)
      : 0);

  const totalCategoryCount = stats?.topCategories?.length ?? categories.length ?? 0;

  if (loading) {
    return (
      <AppShell>
        <MarketingSection theme="deep">
          <GlassPanel padding="lg">
            <div className={styles.emptyState}>Loading marketplace&hellip;</div>
          </GlassPanel>
        </MarketingSection>
      </AppShell>
    );
  }

  return (
    <AppShell heroWatchId="marketplace-hero-sentinel">
      <div className={styles.page}>
        <MarketingSection theme="gradient" collapsePadding>
          <div className={styles.heroGrid}>
            <div className={styles.heroIntro}>
              <SectionHeader
                overline="Marketplace"
                title="Operate a function marketplace that feels bespoke"
                description="Discover, package, and deploy reusable logic from builders worldwide. Relay keeps billing, entitlements, and operations synchronized so you launch faster without losing control."
              />
            </div>
            <div className={styles.heroStats}>
              <div className={styles.statPanel}>
                <span
                  className={styles.statValue}
                  data-testid="marketplace-functions-available"
                >
                  {totalFunctionsAvailable.toLocaleString()}
                </span>
                <span className={styles.statLabel}>Functions available</span>
              </div>
              <div className={styles.statPanel}>
                <span
                  className={styles.statValue}
                  data-testid="marketplace-categories-count"
                >
                  {totalCategoryCount}
                </span>
                <span className={styles.statLabel}>Categories</span>
              </div>
              <div className={styles.statPanel}>
                <span className={styles.statValue}>24/7</span>
                <span className={styles.statLabel}>Support coverage</span>
              </div>
            </div>
          </div>
          <div id="marketplace-hero-sentinel" aria-hidden="true" style={{ height: 1 }} />
        </MarketingSection>

        <MarketingSection theme="dark">
          <GlassPanel padding="lg">
            <div className={styles.searchSection}>
              <SectionHeader
                overline="Search the catalog"
                title="Filter by namespace, pricing model, or sentiment"
                description="Target the exact function you need with marketplace-grade search. Sift through curated collections, pricing tiers, and usage signals in seconds."
              />
              <FunctionSearch
                categories={categories}
                onSearch={handleSearch}
                resultCount={totalResults}
                initialParams={initialSearchParams}
              />
            </div>
          </GlassPanel>
        </MarketingSection>

        {featuredFunctions.length > 0 && !searchParams.get('q') && (
          <MarketingSection theme="deep">
            <div className={styles.sectionHeading}>
              <SparklesIcon aria-hidden="true" />
              <h2>Featured functions</h2>
            </div>
            <div className={styles.cardGrid}>
              {featuredFunctions.slice(0, 8).map((func) => (
                <FunctionCard
                  key={func.id}
                  function={func}
                  onPurchase={handlePurchase}
                  onWishlist={handleWishlist}
                  onPreview={handlePreview}
                  isWishlisted={wishlistItems.has(func.id)}
                />
              ))}
            </div>
          </MarketingSection>
        )}

        {!searchParams.get('q') && (
          <MarketingSection theme="dark">
            <div className={styles.sectionHeading}>
              <TagIcon aria-hidden="true" />
              <h2>Browse by category</h2>
            </div>
            <div className={styles.categoryGrid}>
              {categories.map((category) => (
                <GlassPanel key={category.id} padding="sm">
                  <Link to={`/marketplace?category=${category.id}`} className={styles.categoryLink}>
                    <span className="text-3xl" aria-hidden="true">{category.icon}</span>
                    <span className={styles.categoryLabel}>{category.name}</span>
                    <span className={styles.categoryCount}>{category.count} functions</span>
                  </Link>
                </GlassPanel>
              ))}
            </div>
          </MarketingSection>
        )}

        <MarketingSection theme="deep">
          <div className={styles.resultsHeader}>
            {searchParams.get('q') || Object.keys(getCurrentSearchParams().filters || {}).length > 0 ? (
              <>
                <AdjustmentsHorizontalIcon aria-hidden="true" />
                <h2>Search results</h2>
              </>
            ) : (
              <>
                <ArrowTrendingUpIcon aria-hidden="true" />
                <h2>Popular functions</h2>
              </>
            )}
          </div>

          {functions.length > 0 ? (
            <>
              <div className={styles.cardGrid}>
                {functions.map((func) => (
                  <FunctionCard
                    key={func.id}
                    function={func}
                    onPurchase={handlePurchase}
                    onWishlist={handleWishlist}
                    onPreview={handlePreview}
                    isWishlisted={wishlistItems.has(func.id)}
                  />
                ))}
              </div>

              {hasMore && (
                <div className={styles.loadMore}>
                  <button
                    onClick={loadMore}
                    disabled={searchLoading}
                    className={styles.loadMoreButton}
                    type="button"
                    data-testid="marketplace-load-more"
                  >
                    {searchLoading ? 'Loading…' : 'Load more functions'}
                  </button>
                </div>
              )}
            </>
          ) : searchLoading ? (
            <div className={styles.emptyState}>Searching functions&hellip;</div>
          ) : (
            <GlassPanel padding="lg">
              <div className={styles.emptyState}>
                <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }} aria-hidden="true">
                  🔍
                </div>
                <p>No functions found. Adjust your filters or explore the featured collections.</p>
                <div className={styles.loadMore}>
                  <button
                    onClick={() => handleSearch({ sortBy: 'popularity' })}
                    className={styles.ctaButton}
                    type="button"
                  >
                    <ArrowTrendingUpIcon className="h-4 w-4" aria-hidden="true" />
                    Show popular functions
                  </button>
                </div>
              </div>
            </GlassPanel>
          )}
        </MarketingSection>
      </div>
    </AppShell>
  );
};

export default Marketplace;
